// src/hooks/useTemplateCapture.js
// 템플릿 -> blob
import { useCallback } from "react";
import { toCanvas } from "html-to-image";

export function loadReadyImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => (img.decode ? img.decode().then(() => resolve(img)).catch(() => resolve(img)) : resolve(img));
    img.onerror = reject;
    img.src = src;
  });
}

export function nextFrame() {
  return new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
}

// ✅ 유틸: canvas → Blob
function canvasToBlob(canvas, type = "image/jpeg", quality = 0.9) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))), type, quality);
  });
}

export default function useTemplateCapture({ checked = false, couponImg } = {}) {
  return useCallback(async (index, titlePath, useOptionPath, opts = {}) => {
    const el = document.getElementById(`template_${titlePath}_${useOptionPath}_${index}`);
    if (!el) return null;

    const {
      // 반환 형태: 'blob' | 'dataURL'
      returnType = "blob",
      // mime: 투명 필요 없으면 jpeg 권장
      mime = "image/jpeg",
      quality = 0.9,
      maxPixelRatio = 2,
    } = opts;

    const cursorEls = el.querySelectorAll(".blinking-cursor");
    const editableEls = el.querySelectorAll(".editable-text");
    let couponEl = null;

    // 표시 제거
    cursorEls.forEach(n => { n.classList.remove("blinking-cursor"); n.classList.add("no-blinking"); });
    editableEls.forEach(n => { n.setAttribute("data-prev-border", n.style.border || ""); n.style.border = "none"; });

    try {
      if (checked && couponImg) {
        await loadReadyImage(couponImg);
        couponEl = document.createElement("img");
        couponEl.src = couponImg;
        couponEl.alt = "쿠폰";
        couponEl.className = "w-16 h-16 object-contain";
        Object.assign(couponEl.style, { position: "absolute", bottom: "28px", right: "20px", zIndex: "99" });
        if (getComputedStyle(el).position === "static") el.style.position = "relative";
        el.appendChild(couponEl);
        await nextFrame();
      }

      const rect = el.getBoundingClientRect();
      const pixelRatio = Math.min(maxPixelRatio, window.devicePixelRatio || 1);

      const canvas = await toCanvas(el, {
        cacheBust: true,
        skipFonts: true,               // ✅ 웹폰트 인라인 방지
        pixelRatio,                    // ✅ 과도한 해상도 방지
        width: Math.ceil(rect.width),  // ✅ 사이즈 명시
        height: Math.ceil(rect.height),
        // 필요시 backgroundColor: "#fff",
      });

      if (returnType === "dataURL") {
        // PNG는 quality 무시됨. JPEG 쓸 때만 품질 적용.
        return canvas.toDataURL(mime, quality);
      } else {
        // ✅ 기본: Blob 반환 (권장)
        return await canvasToBlob(canvas, mime, quality);
      }
    } catch (e) {
      console.error("[captureImageFromTemplate] fail:", e);
      return null;
    } finally {
      if (couponEl?.parentNode) el.removeChild(couponEl);
      cursorEls.forEach(n => { n.classList.remove("no-blinking"); n.classList.add("blinking-cursor"); });
      editableEls.forEach(n => { n.style.border = n.getAttribute("data-prev-border") || ""; n.removeAttribute("data-prev-border"); });
    }
  }, [checked, couponImg]);
}
